import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ClosetItemSummary, PrivateShowRequestType } from '@fetmode/types';
import { CLOSET_CATEGORIES } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth, useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';

import { useCoinShop } from '../wallet/index.js';
import { StepFormChrome } from './StepFormChrome.js';
import './PrivateShowRfpModal.css';

export interface PrivateShowRfpModalProps {
  isOpen: boolean;
  onClose: () => void;
  modelUsername: string;
  /** When true, scheduledAt is required. */
  requireSchedule: boolean;
  onSubmitted?: () => void;
  vipEnabled?: boolean;
  exclusiveEnabled?: boolean;
  vipPreTipCoins?: number;
  exclusivePreTipCoins?: number;
}

function toDatetimeLocalValue(date: Date): string {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function defaultScheduleValue(): string {
  return toDatetimeLocalValue(new Date(Date.now() + 60 * 60_000));
}

const STEP_DETAILS = 0;
const STEP_BRIEF = 1;
const STEP_SCHEDULE = 2;
const STEP_CLOSET = 3;
const STEP_OFFER = 4;

export function PrivateShowRfpModal({
  isOpen,
  onClose,
  modelUsername,
  requireSchedule,
  onSubmitted,
  vipEnabled = true,
  exclusiveEnabled = true,
  vipPreTipCoins = 0,
  exclusivePreTipCoins = 0,
}: PrivateShowRfpModalProps): ReactElement | null {
  const { api, user } = useAuth();
  const { openAuthModal, requireAuth } = useAuthModal();
  const { requireSpendCoins } = useCoinShop();
  const [step, setStep] = useState(STEP_DETAILS);
  const [type, setType] = useState<PrivateShowRequestType>('VIP');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [description, setDescription] = useState('');
  const [hardNos, setHardNos] = useState('');
  const [offeredCoins, setOfferedCoins] = useState('200');
  const [scheduledAt, setScheduledAt] = useState(() =>
    requireSchedule ? defaultScheduleValue() : '',
  );
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [closetItems, setClosetItems] = useState<ClosetItemSummary[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const typeOptions = (['VIP', 'EXCLUSIVE'] as const).filter((option) =>
    option === 'VIP' ? vipEnabled : exclusiveEnabled,
  );
  const pretipForType = type === 'EXCLUSIVE' ? exclusivePreTipCoins : vipPreTipCoins;
  const totalSteps = 5;
  const hasCloset = closetItems.length > 0;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setStep(STEP_DETAILS);
    setError(null);
    setSuccess(false);
    setScheduledAt(requireSchedule ? defaultScheduleValue() : '');
    setSelectedKeys(new Set());
    void api
      .getModelCloset(modelUsername)
      .then((result) => {
        setClosetItems(result.items);
      })
      .catch(() => {
        setClosetItems([]);
      });
  }, [api, isOpen, modelUsername, requireSchedule]);

  const itemsByCategory = useMemo(() => {
    return CLOSET_CATEGORIES.map((category) => ({
      category,
      items: closetItems.filter((item) => item.category === category),
    })).filter((group) => group.items.length > 0);
  }, [closetItems]);

  function toggleKey(key: string): void {
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < 20) {
        next.add(key);
      }
      return next;
    });
  }

  function goNext(): void {
    setError(null);
    if (step === STEP_BRIEF && !description.trim()) {
      setError(t('common.show.privateRfpDescriptionRequired'));
      return;
    }
    if (step === STEP_SCHEDULE && requireSchedule && !scheduledAt) {
      setError(t('common.show.privateRfpScheduleRequired'));
      return;
    }
    if (step === STEP_SCHEDULE && !hasCloset) {
      setStep(STEP_OFFER);
      return;
    }
    setStep((current) => Math.min(current + 1, STEP_OFFER));
  }

  function goBack(): void {
    setError(null);
    if (step === STEP_OFFER && !hasCloset) {
      setStep(STEP_SCHEDULE);
      return;
    }
    setStep((current) => Math.max(current - 1, STEP_DETAILS));
  }

  function submit(): void {
    requireAuth(() => {
      const coins = Number.parseInt(offeredCoins, 10);
      if (!description.trim()) {
        setError(t('common.show.privateRfpDescriptionRequired'));
        setStep(STEP_BRIEF);
        return;
      }
      if (!Number.isFinite(coins) || coins < 50) {
        setError(t('common.show.privateRfpCoinsInvalid'));
        return;
      }
      if (requireSchedule && !scheduledAt) {
        setError(t('common.show.privateRfpScheduleRequired'));
        setStep(STEP_SCHEDULE);
        return;
      }

      const runSubmit = (): void => {
        setIsSubmitting(true);
        setError(null);
        void api
          .createPrivateShowRequest({
            modelUsername,
            type,
            durationMinutes,
            description: description.trim(),
            hardNos: hardNos.trim() || undefined,
            offeredCoins: coins,
            scheduledAt:
              scheduledAt.length > 0 ? new Date(scheduledAt).toISOString() : null,
            closetItemKeys: [...selectedKeys],
          })
          .then(() => {
            setSuccess(true);
            onSubmitted?.();
          })
          .catch((err: unknown) => {
            setError(
              err instanceof ApiError ? err.message : t('common.show.privateRfpSubmitError'),
            );
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      };
      void requireSpendCoins(coins, runSubmit).then((ok) => {
        if (ok) {
          runSubmit();
        }
      });
    }, 'private');
  }

  if (!isOpen) {
    return null;
  }

  const stepLabels = [
    t('common.show.privateRfpStepDetails'),
    t('common.show.privateRfpStepBrief'),
    t('common.show.privateRfpStepSchedule'),
    t('common.show.privateRfpStepCloset'),
    t('common.show.privateRfpStepOffer'),
  ] as const;

  return createPortal(
    <div className="show-room-modal private-rfp-modal" role="dialog" aria-modal="true">
      <button
        type="button"
        className="show-room-modal__backdrop"
        aria-label={t('common.show.privateShowModalClose')}
        onClick={onClose}
      />
      <div className="show-room-modal__panel">
        <header className="show-room-modal__header">
          <h2 className="show-room-modal__title">
            {requireSchedule
              ? t('common.show.privateRfpScheduledTitle')
              : t('common.show.privateRfpTitle')}
          </h2>
          <button
            type="button"
            className="show-room-modal__close"
            onClick={onClose}
            aria-label={t('common.show.privateShowModalClose')}
          >
            ×
          </button>
        </header>

        <div className="show-room-modal__body">
          {!user ? (
            <p className="show-room-modal__muted">
              <button
                type="button"
                className="show-room-modal__text-action"
                onClick={() => {
                  openAuthModal({ mode: 'login', intent: 'private' });
                }}
              >
                {t('common.show.privateRfpSignIn')}
              </button>
            </p>
          ) : success ? (
            <p className="private-rfp-modal__success">
              {requireSchedule
                ? t('common.show.privateRfpScheduledSuccess')
                : t('common.show.privateRfpSuccess')}
            </p>
          ) : (
            <>
              <p className="show-room-modal__muted">
                {requireSchedule
                  ? t('common.show.privateRfpScheduledHint')
                  : t('common.show.privateRfpHint')}
              </p>

              <StepFormChrome
                step={step}
                totalSteps={totalSteps}
                stepLabel={stepLabels[step] ?? ''}
                error={error}
                isSubmitting={isSubmitting}
                isLastStep={step === STEP_OFFER}
                submitLabel={
                  isSubmitting
                    ? t('common.show.privateRfpSubmitting')
                    : t('common.show.privateRfpSubmit')
                }
                onBack={goBack}
                onNext={goNext}
                onSubmit={submit}
              >
                {step === STEP_DETAILS ? (
                  <section className="show-room-modal__section">
                    <h3 className="show-room-modal__section-title">{t('common.show.queueType')}</h3>
                    <div className="show-room-modal__types">
                      {typeOptions.map((option) => (
                        <label
                          key={option}
                          className={`show-room-modal__type${type === option ? ' show-room-modal__type--active' : ''}`}
                        >
                          <input
                            type="radio"
                            name="private-rfp-type"
                            checked={type === option}
                            onChange={() => {
                              setType(option);
                            }}
                          />
                          <strong>{option}</strong>
                        </label>
                      ))}
                    </div>
                    <label className="private-rfp-modal__field">
                      {t('common.show.queueDuration')}
                      <input
                        className="fm-input"
                        type="number"
                        min={15}
                        max={240}
                        step={15}
                        value={durationMinutes}
                        onChange={(event) => {
                          setDurationMinutes(Number.parseInt(event.target.value, 10) || 30);
                        }}
                      />
                    </label>
                  </section>
                ) : null}

                {step === STEP_BRIEF ? (
                  <section className="show-room-modal__section">
                    <label className="private-rfp-modal__field">
                      {t('common.show.privateRfpDescription')}
                      <textarea
                        className="fm-input"
                        rows={4}
                        maxLength={500}
                        value={description}
                        onChange={(event) => {
                          setDescription(event.target.value);
                        }}
                      />
                    </label>
                    <label className="private-rfp-modal__field">
                      {t('common.show.privateRfpHardNos')}
                      <input
                        className="fm-input"
                        type="text"
                        maxLength={200}
                        value={hardNos}
                        onChange={(event) => {
                          setHardNos(event.target.value);
                        }}
                      />
                    </label>
                  </section>
                ) : null}

                {step === STEP_SCHEDULE ? (
                  <section className="show-room-modal__section">
                    <label className="private-rfp-modal__field">
                      {requireSchedule
                        ? t('common.show.privateRfpScheduleRequiredLabel')
                        : t('common.show.privateRfpScheduleOptionalLabel')}
                      <input
                        className="fm-input"
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={(event) => {
                          setScheduledAt(event.target.value);
                        }}
                        required={requireSchedule}
                      />
                    </label>
                    {!requireSchedule ? (
                      <p className="show-room-modal__muted">
                        {t('common.show.privateRfpScheduleSkipHint')}
                      </p>
                    ) : null}
                  </section>
                ) : null}

                {step === STEP_CLOSET ? (
                  <section className="show-room-modal__section">
                    <p className="show-room-modal__muted">{t('common.show.privateRfpClosetHint')}</p>
                    {itemsByCategory.map((group) => (
                      <div key={group.category} className="private-rfp-modal__closet-group">
                        <h4 className="private-rfp-modal__closet-category">
                          {t(`common.show.closetCategory.${group.category}`)}
                        </h4>
                        <div className="private-rfp-modal__closet-grid" role="group">
                          {group.items.map((item) => {
                            const selected = selectedKeys.has(item.catalogKey);
                            return (
                              <button
                                key={item.catalogKey}
                                type="button"
                                className={`private-rfp-modal__closet-item${selected ? ' private-rfp-modal__closet-item--selected' : ''}`}
                                aria-pressed={selected}
                                onClick={() => {
                                  toggleKey(item.catalogKey);
                                }}
                              >
                                <span className="private-rfp-modal__closet-icon" aria-hidden>
                                  {item.icon}
                                </span>
                                <span className="private-rfp-modal__closet-label">{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </section>
                ) : null}

                {step === STEP_OFFER ? (
                  <section className="show-room-modal__section">
                    <label className="private-rfp-modal__field">
                      {t('common.show.privateRfpOfferedCoins')}
                      <input
                        className="fm-input"
                        type="number"
                        min={50}
                        value={offeredCoins}
                        onChange={(event) => {
                          setOfferedCoins(event.target.value);
                        }}
                      />
                    </label>
                    {pretipForType > 0 ? (
                      <p className="show-room-modal__muted" style={{ margin: '0.5rem 0 0' }}>
                        {t('common.show.privateRfpPretipNote').replace(
                          '{{amount}}',
                          String(pretipForType),
                        )}
                      </p>
                    ) : null}
                    <ul className="step-form__review">
                      <li>
                        <span>{t('common.show.queueType')}</span>
                        <strong>{type}</strong>
                      </li>
                      <li>
                        <span>{t('common.show.queueDuration')}</span>
                        <strong>{durationMinutes}</strong>
                      </li>
                      {scheduledAt ? (
                        <li>
                          <span>{t('common.show.privateRfpStepSchedule')}</span>
                          <strong>{new Date(scheduledAt).toLocaleString()}</strong>
                        </li>
                      ) : null}
                      {selectedKeys.size > 0 ? (
                        <li>
                          <span>{t('common.show.privateRfpClosetTitle')}</span>
                          <strong>{selectedKeys.size}</strong>
                        </li>
                      ) : null}
                    </ul>
                  </section>
                ) : null}
              </StepFormChrome>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
